import { useEffect, useState } from 'react'
import { Check, ExternalLink, FileText, Loader2 } from 'lucide-react'
import { JarvisOrb } from '@/components/JarvisOrb'
import Seo from '@/components/Seo'
import { apiClient, unwrap } from '@/lib/apiClient'
import type { PublicDigitalProduct } from '@/types'

// Public route. Anyone with the URL — paying or not, logged in or not — can
// see the product offer and click through to Stripe Checkout. Used for "link
// in bio" social posts and as the in-product upsell from the Command Center.

export function PlaybookPage() {
  const [product, setProduct] = useState<PublicDigitalProduct | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    apiClient
      .get('/digital-products')
      .then((r) => unwrap<{ products: PublicDigitalProduct[] }>(Promise.resolve(r)))
      .then((d) => {
        if (cancelled) return
        const match = d.products.find((p) => p.slug === 'jarvis-2day-build')
        setProduct(match ?? null)
        if (!match) setError('Product not found')
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message)
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="min-h-screen bg-jarvis-dark text-jarvis-text">
      <Seo
        title="The Jarvis Playbook — How Owners Use Their AI Chief of Staff"
        description="Day-to-day ways solo owners use Jarvis to track ads, revenue, and morning briefings."
        path="/playbook"
      />
      <header className="border-b border-jarvis-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-jarvis-blue font-semibold tracking-widest text-sm">
            JARVIS
          </a>
          <a
            href="/login"
            className="text-xs text-jarvis-muted hover:text-jarvis-text transition-colors"
          >
            Sign in →
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-jarvis-blue" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-20 text-jarvis-red">{error}</div>
        )}

        {product && (
          <>
            <div className="flex flex-col items-center text-center mb-10">
              <JarvisOrb state="idle" size="md" />
              <div className="text-xs uppercase tracking-widest text-jarvis-blue mt-6">
                The Build Operator Method
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-3">
                {product.name}
              </h1>
              <p className="text-jarvis-muted mt-4 max-w-xl text-base leading-relaxed">
                {product.blurb}
              </p>
            </div>

            <div className="border border-jarvis-border rounded-xl bg-jarvis-surface/60 p-6 md:p-8 mb-8">
              <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
                <div className="flex items-center gap-3">
                  <FileText size={22} className="text-jarvis-blue" />
                  <span className="text-lg font-semibold">What's inside</span>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-jarvis-text">
                    ${(product.priceCents / 100).toFixed(0)}
                  </div>
                  <div className="text-xs text-jarvis-muted">one-time · instant delivery</div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {product.highlights.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm">
                    <Check
                      size={16}
                      className="text-jarvis-green flex-shrink-0 mt-0.5"
                    />
                    <span className="text-jarvis-text/90">{line}</span>
                  </li>
                ))}
              </ul>

              {product.checkoutUrl ? (
                <a
                  href={product.checkoutUrl}
                  className="block w-full text-center bg-jarvis-blue hover:bg-jarvis-blue-bright text-white font-medium py-3.5 rounded-lg transition-colors text-base"
                >
                  Buy now — ${(product.priceCents / 100).toFixed(0)}
                </a>
              ) : (
                <button
                  disabled
                  className="block w-full text-center bg-jarvis-surface-2 text-jarvis-muted py-3.5 rounded-lg cursor-not-allowed"
                >
                  Checkout temporarily unavailable
                </button>
              )}

              <p className="text-xs text-jarvis-muted text-center mt-3">
                Secure checkout via Stripe · PDF delivered to your email within ~5 seconds of purchase
              </p>
            </div>

            <div className="text-xs text-jarvis-muted text-center space-y-1">
              <div>
                Questions before you buy?{' '}
                <a
                  href="mailto:support@yourproject.ai"
                  className="text-jarvis-blue hover:underline"
                >
                  support@yourproject.ai
                </a>
              </div>
              <div>
                Built by{' '}
                <a
                  href="https://yourproject.ai"
                  className="text-jarvis-blue hover:underline inline-flex items-center gap-0.5"
                >
                  KC Hansen <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  )
}
